import { useCallback, useEffect, useRef } from "react";

/**
 * Calls `onFocus` when the browser tab regains visibility,
 * throttled to at most once per `minIntervalMs` (default 30s).
 */
export function useWindowFocusRefresh(
  onFocus: () => void,
  minIntervalMs = 30_000
) {
  const lastRefreshRef = useRef(Date.now());
  const callbackRef = useRef(onFocus);
  callbackRef.current = onFocus;

  const handler = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastRefreshRef.current < minIntervalMs) return;
    lastRefreshRef.current = now;
    callbackRef.current();
  }, [minIntervalMs]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [handler]);
}
